// =============================================================================
// NotificationBody — renders a notification message with clickable links
// =============================================================================
// Announcement rows carry a sanitized `bodyHtml` (server-side strict allow-list
// that already forces target="_blank" rel="noopener noreferrer" on anchors), so
// we render it as HTML and let links be independently clickable. The row itself
// is therefore NOT a single <button> that would swallow those clicks. System
// notifications have no bodyHtml and fall back to plain text.
// =============================================================================

import type { InboxNotification } from '@/lib/notifications';

const BODY_CLASS =
  'text-sm text-text-secondary break-words ' +
  '[&_p]:m-0 [&_p+p]:mt-1 ' +
  '[&_a]:font-medium [&_a]:text-brand-600 [&_a]:underline hover:[&_a]:text-brand-700 ' +
  '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-text-primary [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-text-primary';

export function NotificationBody({
  notification,
  className,
  onInteract,
}: {
  notification: InboxNotification;
  className?: string;
  /** Fired when the user interacts with the body (e.g. clicks a link). */
  onInteract?: () => void;
}) {
  const cls = `${BODY_CLASS}${className ? ` ${className}` : ''}`;

  if (notification.bodyHtml) {
    return (
      <div
        className={cls}
        // Mark-read on any interaction inside the body (link click); does not
        // preventDefault, so the anchor still navigates in a new tab.
        onClickCapture={onInteract}
        // Server-sanitized with a strict allow-list before storage — safe.
        dangerouslySetInnerHTML={{ __html: notification.bodyHtml }}
      />
    );
  }

  if (!notification.body) return null;
  return <p className={cls}>{notification.body}</p>;
}
